import {
  flushAnalytics,
  trackEvent,
  trackPageView
} from "./ga4";
import { errorToMessage, trackFrontendError } from "./frontendErrorTracker";

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const TOKEN_PATTERN = /\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g;

const hasWindow = () => typeof window !== "undefined" && typeof document !== "undefined";

const sanitizeString = (value, limit = 80) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted]")
    .replace(PHONE_PATTERN, "[redacted]")
    .replace(TOKEN_PATTERN, "[redacted]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, limit)
);

const sanitizeKey = (value) => (
  sanitizeString(value, 40)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
);

const safeContext = (input = {}) => {
  if (!input || typeof input !== "object") return {};
  return Object.entries(input).reduce((out, [rawKey, rawValue]) => {
    const key = sanitizeKey(rawKey);
    if (!key) return out;
    if (typeof rawValue === "string") out[key] = sanitizeString(rawValue, 60);
    if (typeof rawValue === "number" && Number.isFinite(rawValue)) out[key] = rawValue;
    if (typeof rawValue === "boolean") out[key] = rawValue;
    return out;
  }, {});
};

const createVitalsObserver = () => {
  if (!hasWindow() || typeof window.PerformanceObserver === "undefined") {
    return { read: () => ({}), stop: () => {} };
  }

  let lcpMs = 0;
  let cls = 0;
  let inpMs = 0;
  let longTaskCount = 0;
  const observers = [];

  const observe = (type, handler) => {
    try {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach(handler));
      observer.observe({ type, buffered: true });
      observers.push(observer);
    } catch {}
  };

  observe("largest-contentful-paint", (entry) => {
    lcpMs = Math.max(lcpMs, Math.round(entry.startTime || 0));
  });
  observe("layout-shift", (entry) => {
    if (!entry.hadRecentInput) cls += Number(entry.value) || 0;
  });
  observe("event", (entry) => {
    if (entry.interactionId) inpMs = Math.max(inpMs, Math.round(entry.duration || 0));
  });
  observe("longtask", () => {
    longTaskCount += 1;
  });

  return {
    read: () => ({
      lcp_ms: lcpMs || undefined,
      cls: cls ? Number(cls.toFixed(3)) : undefined,
      inp_ms: inpMs || undefined,
      long_tasks: longTaskCount || undefined
    }),
    stop: () => observers.forEach((observer) => {
      try { observer.disconnect(); } catch {}
    })
  };
};

const createNoopController = () => ({
  stop: () => {},
  trackVirtualPage: () => false,
  trackFunnelStage: () => false,
  trackMapInteraction: () => false,
  trackCustomAction: () => false
});

export const startBehaviorTracking = ({ getContext } = {}) => {
  if (!hasWindow()) return createNoopController();

  const startedAt = Date.now();
  let visibleStartedAt = document.visibilityState === "visible" ? Date.now() : 0;
  let visibleMs = 0;
  let lastPage = "";
  let summarySent = false;
  const seenFunnelStages = new Set();
  const seenMapInteractions = new Set();
  const vitals = createVitalsObserver();

  const context = () => safeContext(typeof getContext === "function" ? getContext() : {});

  const updateVisibleTime = () => {
    if (visibleStartedAt > 0) visibleMs += Math.max(0, Date.now() - visibleStartedAt);
    visibleStartedAt = document.visibilityState === "visible" ? Date.now() : 0;
  };

  const onVisibilityChange = () => {
    updateVisibleTime();
  };

  const onError = (event) => {
    trackFrontendError({
      type: "runtime",
      source: "window_error",
      message: sanitizeString(event?.message || "runtime_error", 90),
      route: context().route || lastPage,
      sourcePath: sanitizeString(event?.filename || "", 80),
      line: Number(event?.lineno) || undefined,
      column: Number(event?.colno) || undefined
    });
  };

  const onUnhandledRejection = (event) => {
    trackFrontendError({
      type: "promise_rejection",
      source: "window_unhandled_rejection",
      message: sanitizeString(errorToMessage(event?.reason), 90),
      route: context().route || lastPage
    });
  };

  const sendSummary = (reason = "pagehide") => {
    if (summarySent) return;
    summarySent = true;
    updateVisibleTime();
    const sessionMs = Math.max(1, Date.now() - startedAt);
    const vitalValues = vitals.read();
    if (Object.values(vitalValues).some((value) => value !== undefined)) {
      trackEvent("web_vitals", { ...context(), ...vitalValues });
    }
    trackEvent("session_summary", {
      ...context(),
      reason,
      session_sec: Math.round(sessionMs / 1000),
      engaged_sec: Math.round(visibleMs / 1000)
    });
    flushAnalytics();
  };

  const onPageHide = () => sendSummary("pagehide");

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
  window.addEventListener("pagehide", onPageHide);

  const trackVirtualPage = (pageName, params = {}) => {
    const route = sanitizeKey(pageName || "unknown_route");
    if (!route || route === lastPage) return false;
    lastPage = route;
    return trackPageView(`CASADATA ${route.replace(/_/g, " ")}`, {
      ...context(),
      ...safeContext(params),
      app_route: route
    });
  };

  const trackFunnelStage = (stage, params = {}) => {
    const stageKey = sanitizeKey(stage || "");
    if (!stageKey || seenFunnelStages.has(stageKey)) return false;
    seenFunnelStages.add(stageKey);
    return trackEvent("funnel_stage_reached", {
      ...context(),
      ...safeContext(params),
      stage: stageKey
    });
  };

  const trackMapInteraction = (interactionType, params = {}) => {
    const interaction = sanitizeKey(interactionType || "");
    const route = context().route || lastPage || "map_workspace";
    const signature = `${route}|${interaction}`;
    if (!interaction || seenMapInteractions.has(signature)) return false;
    seenMapInteractions.add(signature);
    return trackEvent("map_interaction", {
      ...context(),
      ...safeContext(params),
      interaction_type: interaction
    });
  };

  const trackCustomAction = (actionName, params = {}) => {
    const action = sanitizeKey(actionName || "");
    if (!action || action.startsWith("db_")) return false;
    return trackEvent(action, { ...context(), ...safeContext(params) });
  };

  const stop = () => {
    sendSummary("manual_stop");
    vitals.stop();
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    window.removeEventListener("pagehide", onPageHide);
  };

  return {
    stop,
    trackVirtualPage,
    trackFunnelStage,
    trackMapInteraction,
    trackCustomAction
  };
};

export default startBehaviorTracking;